import type { SetOperatorName } from "./types";

function reverseMap<T1 extends string, T2 extends string>(map: Record<T1, T2>): Record<T2, T1> {
  return Object.entries(map).reduce(
    (acc, [key, v]) => {
      const value = v as T2;
      if (!(value in acc)) {
        acc[value] = key as T1;
      }
      return acc;
    },
    {} as Record<T2, T1>,
  );
}

// Function identity is a fully-qualified name (`<module>.<attr>`) matching what the Python serializer
// emits (see sympy_json.sympy_to_json). semanticToSympyCode maps the module to its Python alias
// (sympy → sp); no prefix is stored/stripped anywhere else.
export const FUNCTION_KEY_TO_SYMPY_MAP = {
  "function-sine": "sp.sin",
  "function-cosine": "sp.cos",
  "function-tangent": "sp.tan",
  "function-cotangent": "sp.cot",
  "function-secant": "sp.sec",
  "function-cosecant": "sp.csc",
  "function-arcsine": "sp.asin",
  "function-arccosine": "sp.acos",
  "function-arctangent": "sp.atan",
  "function-arccotangent": "sp.acot",
  "function-arcsecant": "sp.asec",
  "function-arccosecant": "sp.acsc",
  "function-atan-2": "sp.atan2",
  "function-hyperbolic-sine": "sp.sinh",
  "function-hyperbolic-cosine": "sp.cosh",
  "function-hyperbolic-tangent": "sp.tanh",
  "function-hyperbolic-cotangent": "sp.coth",
  "function-hyperbolic-secant": "sp.sech",
  "function-hyperbolic-cosecant": "sp.csch",
  // Inverse hyperbolic functions
  "function-hyperbolic-arcsine": "sp.asinh",
  "function-hyperbolic-arccosine": "sp.acosh",
  "function-hyperbolic-arctangent": "sp.atanh",
  "function-hyperbolic-arccotangent": "sp.acoth",
  "function-hyperbolic-arcsecant": "sp.asech",
  "function-hyperbolic-arccosecant": "sp.acsch",
  "function-exponential-function": "sp.exp",
  "function-logarithm": "sp.log",
  "function-natural-logarithm": "sp.log",
  "function-absolute-value": "sp.Abs",
  "function-sqrt": "sp.sqrt",
  "function-sign": "sp.sign",
  "function-greatest-common-divisor": "sp.gcd",
  "function-least-common-multiple": "sp.lcm",
  "operator-mod": "sp.Mod",
  "function-min": "sp.Min",
  "function-max": "sp.Max",
  "function-re": "sp.re",
  "function-im": "sp.im",
  "function-conjugate": "sp.conjugate",
  "function-arg": "sp.arg",

  "function-determinant": "sp.Determinant",
  "function-transpose": "sp.Transpose",
  "function-union-of-sets": "sp.Union",
  "function-intersection-of-sets": "sp.Intersection",
  "function-finite-set": "sp.FiniteSet",
  "function-gamma-function": "sp.gamma",
  "function-dirac-function": "sp.DiracDelta",
  "function-kronecker-delta": "sp.KroneckerDelta",
  "function-riemann-zeta-function": "sp.zeta",
  "function-euler-totient-function": "sp.totient",
  "function-n-ary-logical-and": "sp.And",
  "function-n-ary-logical-or": "sp.Or",
  "function-disjoint-union": "sp.Union",

  // Custom classes (sympy_json.*) use their CamelCase class name — matching expr.func.__name__ on
  // output — so stock and custom functions share this one bidirectional map.
  "function-arithmetic-mean": "sympy_json.statistics.Mean",
  "function-median": "sympy_json.statistics.Median",
  "function-mode": "sympy_json.statistics.Mode",
  "function-variance": "sympy_json.statistics.Variance",
  "function-standard-deviation": "sympy_json.statistics.Std",
  "function-sample-variance": "sympy_json.statistics.SampleVariance",
  "function-sample-standard-deviation": "sympy_json.statistics.SampleStd",
  "function-covariance": "sympy_json.statistics.Covariance",
  "function-correlation": "sympy_json.statistics.Correlation",
  "function-absolute-deviation": "sympy_json.statistics.AbsoluteDeviation",
  "function-geometric-mean": "sympy_json.statistics.GeometricMean",
  "function-arithmetic-geometric-mean": "sympy_json.statistics.ArithmeticGeometricMean",
  "function-harmonic-mean": "sympy_json.statistics.HarmonicMean",
} satisfies Record<string, string>;

export const FUNCTION_SYMPY_TO_KEY_MAP = reverseMap(FUNCTION_KEY_TO_SYMPY_MAP);

export const OPERATOR_KEY_TO_SYMPY_MAP: Record<string, string> = {
  "operator-factorial": "sp.factorial",
};

export const OPERATOR_SYMPY_TO_KEY_MAP = reverseMap(OPERATOR_KEY_TO_SYMPY_MAP);

// Values are the bare SymPy class names that appear in output (expr.func.__name__) — constantExpr
// switches on them and emitOperator holds the singleton emit spellings (Pi → sp.pi, etc.).
export const CONSTANT_TO_SYMPY_MAP: Record<string, string> = {
  "symbol-pi": "Pi",
  "constant-eulers-number": "Exp1",
  "constant-infinity": "oo",
  "constant-imaginary-number-i": "I",
  "constant-golden-ratio": "GoldenRatio",
  "constant-catalans-constant": "Catalan",
  "constant-imaginary-number-j": "I",
  "constant-empty-set": "EmptySet",
};

// Blackboard-bold set symbols (ℝ ℤ ℕ ℚ ℂ) map to SymPy's predefined sets.
// These render as `variable` nodes in the editor, so the mapping is applied in
// the variable branch of traverseNode, not only for constant nodes.
export const SET_SYMBOL_TO_SYMPY_MAP: Record<string, SetOperatorName> = {
  "symbol-double-struck-r": "Reals",
  "symbol-double-struck-z": "Integers",
  "symbol-double-struck-n": "Naturals",
  "symbol-double-struck-q": "Rationals",
  "symbol-double-struck-c": "Complexes",
};

// Back-path: SymPy returns predefined sets as bare operator names (e.g.
// "Integers"), which map back to the editor's blackboard-bold symbol keys.
export const SYMPY_SET_NAME_TO_SYMBOL_KEY: Record<string, string> = {
  Reals: "symbol-double-struck-r",
  Integers: "symbol-double-struck-z",
  Naturals: "symbol-double-struck-n",
  Naturals0: "symbol-double-struck-n",
  Rationals: "symbol-double-struck-q",
  Complexes: "symbol-double-struck-c",
};

/** How the inline folder treats an infix operator symbol (see `classifyInfix` / `foldInline`). */
export type InfixKind =
  | "plus"
  | "minus"
  | "mul"
  | "div"
  | "union"
  | "intersection"
  | "setdiff"
  | "other";

/**
 * Maps infix operator symbol-keys to how the inline folder treats them. Keyed by symbol-key rather
 * than by rendered glyph, because glyphs don't identify an operator: the palette spells several
 * distinct operators the same way (`⋅` is multiplication and dot product, `∨` is join and logical
 * or), and the rest of the ~300 operator symbols have no arithmetic meaning at all.
 */
export const OPERATOR_SYMBOL_TO_INFIX_KIND: Record<string, InfixKind> = {
  "symbol-plus": "plus",
  "symbol-subtraction": "minus",
  "symbol-division": "div",
  "symbol-divide-by": "div",
  "symbol-multiplication-dot": "mul",
  "symbol-bullet-operator": "mul",
  "symbol-multiplication-times": "mul",
  "symbol-union": "union",
  "symbol-intersection": "intersection",
  "symbol-set-difference": "setdiff",
};

/**
 * Fallback classification by rendered glyph, for operator symbols that carry no `key` — editor input
 * always does, but hand-built and programmatically created nodes may not. Deliberately limited to
 * the four glyphs that can only mean arithmetic; e.g. `|` renders the "divides" relation, not union.
 */
export const OPERATOR_GLYPH_TO_INFIX_KIND: Record<string, InfixKind> = {
  "+": "plus",
  "-": "minus",
  "*": "mul",
  "/": "div",
};

/**
 * Functions that can be "solved" (evaluated) and require special parsing.
 * These are handled in buildSympyCode.ts with custom traversal logic.
 */
export const SOLVABLE_FUNCTION_KEYS = new Set([
  "function-integral",
  "function-indefinite-integral",
  "function-limit",
  "function-summation",
  "function-product",
  "function-transpose",
]);

/**
 * Maps relational operator symbol-keys to the bare SymPy class name used for `<lhs> <op> <rhs>`.
 * We use functional form (e.g. `sp.StrictLessThan(a, b)`) instead of raw infix so the emitted code
 * is unambiguous and matches the class names the result parser sees coming back from the SymPy
 * serializer. The `sp.` prefix is added by the emitter.
 */
export const OPERATOR_SYMBOL_TO_SYMPY_RELATION_MAP: Record<string, string> = {
  "symbol-equal": "Equality",
  "symbol-less-than": "StrictLessThan",
  "symbol-greater-than": "StrictGreaterThan",
  "symbol-less-than-or-equal-to": "LessThan",
  "symbol-greater-than-or-equal": "GreaterThan",
};

/**
 * Trigonometric and hyperbolic function node keys. Bare numeric args to these
 * functions are treated as radians and evaluated numerically (see buildSympyCode.ts).
 * Includes inverses (arc-prefix) and hyperbolic variants.
 */
// Funcs needing numeric (sp.N) eval for bare-numeric args like sin(5). Identity-expanded
// funcs (versine, haversine, etc.) are excluded: they simplify to exact rationals.
export const TRIG_FUNCTION_KEYS = new Set([
  "function-sine",
  "function-cosine",
  "function-tangent",
  "function-cotangent",
  "function-secant",
  "function-cosecant",
  "function-arcsine",
  "function-arccosine",
  "function-arctangent",
  "function-arccotangent",
  "function-arcsecant",
  "function-arccosecant",
  "function-atan-2",
  "function-hyperbolic-sine",
  "function-hyperbolic-cosine",
  "function-hyperbolic-tangent",
  "function-hyperbolic-cotangent",
  "function-hyperbolic-secant",
  "function-hyperbolic-cosecant",
  "function-hyperbolic-arcsine",
  "function-hyperbolic-arccosine",
  "function-hyperbolic-arctangent",
  "function-hyperbolic-arccotangent",
  "function-hyperbolic-arcsecant",
  "function-hyperbolic-arccosecant",
]);
